import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, IconButton, Tooltip, Button, Menu, MenuItem, Checkbox, Chip } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import refreshIcon from '../../assets/images/refresh_icon.svg';
import emailSettingBg from '../../assets/images/email_setting_bg.svg';

import Navbar from '../shared/Navbar';
import OverflowTooltip from '../../shared/OverflowTooltip';
import ReportsTab from './tabs/ReportsTab';
import { fetchProjectDropdownsByUser } from '../../store/ticketSlice';
import LogStandupTab from './tabs/LogStandupTab';
import SettingsTab from './tabs/SettingsTab';
import './DailyStandup.scss';

const DailyStandup = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  
  const [activeTab, setActiveTab] = useState('reports');
  const { report: dailyStandupReport, loading: reportLoading, standupLogLoading } = useSelector((state) => state.dailyStandup || {});
  const { filters, projectDropdownsByUser, projectDropdownsByUserLoading, projectDropdownsByUserError, lastSelectedProjectId } = useSelector((state) => state.tickets);
  const [logStandupKey, setLogStandupKey] = useState(0);
  const [logStandupRefreshKey, setLogStandupRefreshKey] = useState(0);
  
  const selectedProjectId = filters?.selectedProject;
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  const effectiveProjectId = useMemo(() => {
    if (selectedProjectId && String(selectedProjectId).toUpperCase() !== 'ALL') {
      return selectedProjectId;
    }
    return lastSelectedProjectId || projectDropdownsByUser?.[0]?.id || null;
  }, [selectedProjectId, lastSelectedProjectId, projectDropdownsByUser]);

  const selectedProject = useMemo(() => {
    if (!effectiveProjectId) return null;
    return projectDropdownsByUser?.find?.((item) => String(item.id) === String(effectiveProjectId)) || null;
  }, [effectiveProjectId, projectDropdownsByUser]);

  const isScrumProject = String(selectedProject?.project_type || '').toUpperCase() === 'SCRUM';

  const activeSprintName =
    dailyStandupReport?.report?.sprint?.name ||
    dailyStandupReport?.report?.sprint?.sprint_name;

  const isSprintNull = !reportLoading && !dailyStandupReport?.report?.sprint;
  const shouldDisableRefresh = activeTab === 'reports' && isScrumProject && isSprintNull;

  const selectedProjectName = selectedProject?.name || filters?.selectedProjectName || 'Select Project';

  useEffect(() => {
    if (!projectDropdownsByUserLoading && !projectDropdownsByUserError && (!projectDropdownsByUser || projectDropdownsByUser.length === 0)) {
      dispatch(fetchProjectDropdownsByUser());
    }
  }, [dispatch, projectDropdownsByUser, projectDropdownsByUserLoading, projectDropdownsByUserError]);

  const handleRefresh = () => {
    if (activeTab === 'logStandup') {
      if (selectedLogProjects.length > 0) {
        setLogStandupRefreshKey((prev) => prev + 1);
      }
      return;
    }

    if (effectiveProjectId) {
      setReportRefreshKey((prev) => prev + 1);
    }
  };

  const tabs = [
    { id: 'reports', label: t('dailyStandup.tabs.reports') },
    { id: 'logStandup', label: t('dailyStandup.tabs.logStandup') },
    { id: 'settings', label: t('dailyStandup.tabs.settings') }
  ];

  const [selectedLogProjects, setSelectedLogProjects] = useState([]);
  const [tempSelectedLogProjects, setTempSelectedLogProjects] = useState([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // Auto-select first project for Log Standup multi-select if empty, prioritizing global sync
  useEffect(() => {
    if (selectedLogProjects.length === 0) {
      if (selectedProjectId && String(selectedProjectId).toUpperCase() !== 'ALL') {
        setSelectedLogProjects([selectedProjectId]);
      } else if (projectDropdownsByUser && projectDropdownsByUser.length > 0) {
        setSelectedLogProjects([projectDropdownsByUser[0].id]);
      }
    }
  }, [projectDropdownsByUser, selectedProjectId, selectedLogProjects.length]);

  const handleFilterClick = (event) => {
    setTempSelectedLogProjects([...selectedLogProjects]);
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleProjectToggle = (projectId) => {
    setTempSelectedLogProjects(prev => {
      if (prev.includes(projectId)) {
        // Prevent deselecting the last project
        if (prev.length === 1) return prev;
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleApplyFilter = () => {
    setSelectedLogProjects([...tempSelectedLogProjects]);
    handleFilterClose();
  };

  const getLogStandupProjectInfo = () => {
    if (!selectedLogProjects || selectedLogProjects.length === 0) {
      return { fullName: t('dailyStandup.selectProject'), displayName: t('dailyStandup.selectProject'), hasMultiple: false, extraCount: 0 };
    }
    const names = selectedLogProjects.map(id => {
      const p = projectDropdownsByUser?.find(proj => String(proj.id) === String(id));
      return p ? p.name : '';
    }).filter(Boolean);
    
    if (names.length === 0) {
      return { fullName: t('dailyStandup.selectProject'), displayName: t('dailyStandup.selectProject'), hasMultiple: false, extraCount: 0 };
    }

    return {
      fullName: names.join(', '),
      displayName: names[0],
      hasMultiple: names.length > 1,
      extraCount: names.length - 1
    };
  };

  const handleTabClick = React.useCallback((tabId) => {
    if (activeTab === tabId) {
      if (tabId === 'logStandup') {
        setLogStandupKey(prev => prev + 1);
      } else if (tabId === 'reports') {
        setReportRefreshKey(prev => prev + 1);
      }
    } else {
      if (tabId === 'logStandup' && !projectDropdownsByUserLoading && !projectDropdownsByUserError && (!projectDropdownsByUser || projectDropdownsByUser.length === 0)) {
        dispatch(fetchProjectDropdownsByUser());
      }
      setActiveTab(tabId);
    }
  }, [activeTab, dispatch, projectDropdownsByUser, projectDropdownsByUserLoading, projectDropdownsByUserError]);

  const handleLogStandupClick = React.useCallback(() => {
    if (activeTab === 'logStandup') setLogStandupKey(prev => prev + 1);
    if (activeTab !== 'logStandup' && !projectDropdownsByUserLoading && !projectDropdownsByUserError && (!projectDropdownsByUser || projectDropdownsByUser.length === 0)) {
      dispatch(fetchProjectDropdownsByUser());
    }
    setActiveTab('logStandup');
  }, [activeTab, dispatch, projectDropdownsByUser, projectDropdownsByUserLoading, projectDropdownsByUserError]);

  const todayStr = new Date().toLocaleDateString(i18n.language || 'en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const projectInfo = getLogStandupProjectInfo();

  return (
    <div className="dashboard">
      <Navbar />
      <main className="main daily-standup-main-container">
        <div className="daily-standup-header">
          {activeTab === 'settings' && (
            <img src={emailSettingBg} className="header-bg-decoration" alt="" />
          )}
          
          {activeTab === 'reports' ? (
            <>
              <Typography className="header-title">
                {t('dailyStandup.title')}
              </Typography>
              
              <div className="header-actions">
                <OverflowTooltip 
                  title={selectedProjectName || t('dailyStandup.selectProject')}
                  placement="bottom"
                  tooltipWidth={400}
                  className="project-sprint-name"
                  sx={{ maxWidth: '250px' }}
                >
                  {selectedProjectName || t('dailyStandup.selectProject')}
                </OverflowTooltip>
                
                {isScrumProject && (
                  <>
                    <Box className="header-divider" />
                    <OverflowTooltip 
                      title={activeSprintName || t('dailyStandup.noActiveSprint')}
                      placement="bottom"
                      tooltipWidth={400}
                      className="project-sprint-name"
                      sx={{ maxWidth: '250px' }}
                    >
                      {activeSprintName || t('dailyStandup.noActiveSprint')}
                    </OverflowTooltip>
                  </>
                )}
                
                <Tooltip title={t('dashboard.refresh')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton 
                    onClick={handleRefresh} 
                    className="dashboard-header-icon-button" 
                    aria-label={t('dashboard.refresh')}
                    disabled={shouldDisableRefresh || reportLoading}
                  >
                    <img src={refreshIcon} alt="Refresh" className={`dashboard-header-icon ${reportLoading ? 'spinning' : ''}`} />
                  </IconButton>
                </Tooltip>
              </div>
            </>
          ) : activeTab === 'settings' ? (
            <Typography className="header-title">
              {t('dailyStandup.tabs.settings')}
            </Typography>
          ) : (
            <>
              <Box className="log-standup-info-container">
                <OverflowTooltip 
                  title={projectInfo.fullName} 
                  placement="bottom-end"
                  tooltipWidth={500}
                  className="log-standup-project-name"
                >
                  {projectInfo.displayName}
                </OverflowTooltip>
                {projectInfo.hasMultiple && (
                  <Tooltip title={projectInfo.fullName} placement="bottom-end" arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                    <Chip 
                      label={`+${projectInfo.extraCount}`} 
                      className="log-standup-multi-chip"
                    />
                  </Tooltip>
                )}
                <Typography className="log-standup-date">
                  • {todayStr}
                </Typography>
              </Box>

              <div className="header-actions">
                <Tooltip title={t('dashboard.refresh')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                  <IconButton 
                    onClick={handleRefresh} 
                    className="dashboard-header-icon-button" 
                    aria-label={t('dashboard.refresh')}
                    disabled={standupLogLoading}
                  >
                    <img src={refreshIcon} alt="Refresh" className={`dashboard-header-icon ${standupLogLoading ? 'spinning' : ''}`} />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="outlined"
                  onClick={handleFilterClick}
                  startIcon={<FilterList />}
                  className="log-standup-filter-btn"
                >
                  {t('dailyStandup.logStandup.projectFilter')}
                </Button>
                
                <Menu
                  anchorEl={filterAnchorEl}
                  open={Boolean(filterAnchorEl)}
                  onClose={handleFilterClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  PaperProps={{ className: 'daily-standup-dropdown-menu' }}
                  MenuListProps={{ className: 'daily-standup-menu-list' }}
                >
                  {projectDropdownsByUser?.map((project) => (
                    <MenuItem key={project.id} onClick={() => handleProjectToggle(project.id)} className="daily-standup-menu-item">
                      <Checkbox checked={tempSelectedLogProjects.includes(project.id)} size="small" className="daily-standup-menu-checkbox" />
                      <OverflowTooltip title={project.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start">
                        {project.name}
                      </OverflowTooltip>
                    </MenuItem>
                  ))}
                  <Box className="daily-standup-apply-container">
                    <Button 
                      variant="contained" 
                      size="small" 
                      onClick={handleApplyFilter} 
                      className="daily-standup-apply-btn"
                      disabled={standupLogLoading}
                    >
                      {t('common.apply')}
                    </Button>
                  </Box>
                </Menu>

              </div>
            </>
          )}
        </div>

        <div className="daily-standup-tabs">
          {tabs.map(tab => (
            <div 
              key={tab.id}
              className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.label}
            </div>
          ))}
        </div>

        <div className={`daily-standup-content ${activeTab === 'settings' ? 'is-settings-tab' : ''}`}>
          {activeTab === 'logStandup' && <div />}
          {activeTab === 'reports' && (
            <ReportsTab 
              onLogStandupClick={handleLogStandupClick}
              refreshKey={reportRefreshKey}
              projectId={effectiveProjectId}
            />
          )}
          {activeTab === 'logStandup' && (
            <LogStandupTab
              key={logStandupKey}
              refreshKey={logStandupRefreshKey}
              selectedProjects={selectedLogProjects}
            />
          )}
          {activeTab === 'settings' && <SettingsTab selectedProjectId={effectiveProjectId} projectDropdownsByUser={projectDropdownsByUser} />}
        </div>
      </main>
    </div>
  );
};

export default DailyStandup;
